import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Ticket, CalendarPlus, Download, FileText, Mail, MessageCircle } from "lucide-react";
import { BOOKINGS_EMAIL } from "@/lib/site";
import { sharePdfOnWhatsApp } from "@/lib/share";

const DOWNLOADS = [
  { label: "Birthday Parties Fact Sheet", href: "/birthday-parties-fact-sheet.pdf", note: "Everything to know", thumb: "/party/fact-sheet-front.png", testid: "download-fact-sheet" },
  { label: "Party Invite", href: "/party-invite-2024.pdf", note: "Printable invites", thumb: "/party/invite-front.png", testid: "download-party-invite" },
];

export default function PartyPlanner() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-20" data-testid="party-planner-section">
      <div className="text-center">
        <h2 className="text-4xl leading-tight text-[#c87700] drop-shadow-[0_1px_0_rgba(122,86,0,0.6)] sm:text-5xl" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>JUST RIDING, OR BOOKING A PARTY?</h2>
      </div>

      <div className="mt-10 grid gap-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border-2 border-[#f8b818]/35 bg-[#14120c] p-8 shadow-[0_14px_35px_rgba(36,28,13,0.22)] transition-all hover:-translate-y-1 hover:border-[#f8b818]"
          data-testid="booking-flow-ride-only-card"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#22c55e]/20 text-[#4ade80]">
            <Ticket size={24} />
          </span>
          <h3 className="mt-4 text-3xl leading-tight text-[#fff7e8]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>JUST RIDES — NO BOOKING NEEDED</h3>
          <p className="mt-3 text-[17px] leading-relaxed text-[#fff7e8]/75">
            Rock up any day — no reservation, no entrance fee, no parking fee. Buy rides online and save 10%, or
            pay at the ticket office on the day. Rides never expire.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/pricing" className="btn-gold" data-testid="ride-only-pricing-btn">
              See prices
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="rounded-3xl border-2 border-[#f8b818]/35 bg-[#14120c] p-8 shadow-[0_14px_35px_rgba(36,28,13,0.22)] transition-all hover:-translate-y-1 hover:border-[#f8b818]"
          data-testid="booking-flow-party-card"
        >
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f8b818]/20 text-[#f8b818]">
            <CalendarPlus size={24} />
          </span>
          <h3 className="mt-4 text-3xl leading-tight text-[#f8b818]" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>PARTY WITH SEATING &amp; CATERING</h3>
          <p className="mt-3 text-[17px] leading-relaxed text-[#fff7e8]/75">
            Email{" "}
            <a href={`mailto:${BOOKINGS_EMAIL}`} className="font-extrabold text-[#f8b818] underline underline-offset-2 hover:underline">
              {BOOKINGS_EMAIL}
            </a>{" "}
            with your preferred date, arrival time and an alternative date in case of rain. We'll confirm availability and guide you through the booking process.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href={`mailto:${BOOKINGS_EMAIL}?subject=Party booking`} className="btn-gold" data-testid="party-email-btn">
              <Mail size={16} /> Email the front desk
            </a>
          </div>
        </motion.div>
      </div>

      <div className="mt-12 rounded-[32px] bg-gradient-to-b from-[#f9c92e] to-[#f8b818] p-8 shadow-[0_24px_70px_rgba(248,184,24,0.4)] sm:p-12" data-testid="party-downloads-banner">
        <h3 className="flex items-center justify-center gap-3 text-3xl text-[#0a0a0b] sm:text-4xl" data-testid="party-downloads-heading" style={{ fontFamily: '"Goodgirl", "Outfit", sans-serif' }}>
          <FileText size={30} className="shrink-0" /> PARTY DOWNLOADS
        </h3>
        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          {DOWNLOADS.map((d) => (
            <div
              key={d.href}
              className="flex items-center justify-between gap-3 rounded-2xl bg-[#fff7e8] p-5 shadow-[0_10px_30px_rgba(36,28,13,0.15)] transition-all hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(36,28,13,0.25)]"
              data-testid={`${d.testid}-card`}
            >
              <a
                href={d.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex min-w-0 flex-1 items-center justify-between gap-4"
                data-testid={d.testid}
              >
                <img
                  src={d.thumb}
                  alt={`${d.label} — first page preview`}
                  loading="lazy"
                  className="h-24 w-[68px] shrink-0 rounded-lg border border-[#241c0d]/10 bg-white object-cover object-top shadow-[0_4px_14px_rgba(36,28,13,0.18)] transition-transform group-hover:scale-[1.04]"
                  data-testid={`${d.testid}-thumb`}
                />
                <span className="min-w-0">
                  <span className="block text-base font-extrabold text-[#241c0d]">{d.label}</span>
                  <span className="mt-0.5 block text-xs font-bold text-[#6b5a3a]">{d.note}</span>
                </span>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#f8b818] text-[#0a0a0b] transition-transform group-hover:scale-110">
                  <Download size={18} />
                </span>
              </a>
              <button
                type="button"
                onClick={() => sharePdfOnWhatsApp(d.label, d.href)}
                aria-label={`Share ${d.label} on WhatsApp`}
                title="Share on WhatsApp"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#25d366] text-white transition-transform hover:scale-110"
                data-testid={`${d.testid}-whatsapp`}
              >
                <MessageCircle size={17} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
